import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FilterModel } from 'src/app/@core/models/filter.model';
import { ResponseResult } from 'src/app/@core/models/response.model';
import { BaseCourseModel } from 'src/app/course/models';
import { ProgramModel } from 'src/app/course/models/program.model';
import { StudentCourseService } from 'src/app/course/services/student-course.service';
import { LanguageEnum } from '../../models/item.model';
import { LanguageService } from 'src/app/services/language.service';
import { LearningPathService } from 'src/app/learning-path/services/learning-path.service';
import { DisplayInHomeFilterModel } from 'src/app/home/models/display-filter.model';
import { Status } from 'src/app/@core/enums/status.enum';
import { Router } from '@angular/router';
import { IdentityService } from 'src/app/@core/services/identity.service';

type SearchShowAllType = 'courses' | 'paths' | 'diplomas';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {
  @Input() showSearch: boolean = false;
  isRtl: boolean = true;
  searchValue: string = "";
  /** Requests still in flight for the current term; the no-match copy is shown only once it reaches 0. */
  pendingRequests: number = 0;
  courses: ResponseResult<Array<BaseCourseModel>> = new ResponseResult<Array<BaseCourseModel>>();
  paths: ResponseResult<Array<ProgramModel>> = new ResponseResult<Array<ProgramModel>>();
  diplomas: ResponseResult<Array<ProgramModel>> = new ResponseResult<Array<ProgramModel>>();
  filterCourses: FilterModel = new FilterModel(1, 4);
  filter: DisplayInHomeFilterModel = new DisplayInHomeFilterModel();
  @Output() hideSearch: EventEmitter<boolean> = new EventEmitter(true);

  /** Unified Code mode sends "Show all" to the search results page (criterion 10); Custom keeps the course list. */
  unified: boolean = false;

  constructor(private languageService: LanguageService, private studentCourseService: StudentCourseService, private pathDiplomaService: LearningPathService,
    private identityService: IdentityService, private router: Router) {
    this.unified = this.identityService.isUnifiedCode();
  };

  get showAllLink(): string[] {
    return this.unified ? ['/search'] : ['/course'];
  }

  showAllParams(type: SearchShowAllType): Record<string, string> {
    if (this.unified) return { q: this.searchValue, type };
    if (type === 'paths') return { path: 'true', search: this.searchValue };
    if (type === 'diplomas') return { diploma: 'true', search: this.searchValue };
    return { search: this.searchValue };
  }

  /** Enter in the header search opens the full results page (Unified Code mode only). */
  openResultsPage(): void {
    const q = (this.searchValue ?? '').trim();
    if (!this.unified || !q) return;
    this.resetSearch();
    this.router.navigate(['/search'], { queryParams: { q } });
  }

  ngOnInit(): void {
    this.isRtl = this.languageService.getCurrentLanguage() === LanguageEnum.ar;
    this.languageService.languageChange.subscribe((result: LanguageEnum) => (this.isRtl = result === LanguageEnum.ar));
  }
  
  onSearch(): void {
    if(this.searchValue && this.searchValue.length > 2){
      this.filter.showInHome = false;
      this.filter.enablePagination = false;
      this.filterCourses.search = this.searchValue;
      this.filter.search = this.searchValue;
      this.pendingRequests = 3;
      this.getCourses();
      this.getPaths();
      this.getDiplomas();
    } else {
      this.pendingRequests = 0;
      this.courses.data = [];
      this.paths.data = [];
      this.diplomas.data = [];
    }
  }

  /** E2E-ISSUE-108 (2): a searched term with no course, path or diploma match shows an explicit message. */
  get noMatch(): boolean {
    return !!this.searchValue && this.searchValue.length > 2 && this.pendingRequests === 0
      && !(this.courses.data?.length > 0) && !(this.paths.data?.length > 0) && !(this.diplomas.data?.length > 0);
  }

  private requestDone(): void {
    if (this.pendingRequests > 0) this.pendingRequests--;
  }

  getCourses(): void {
    this.filterCourses.status = Status.Active;
    this.studentCourseService.getPaging(this.filterCourses, 'forDiploma=false').subscribe({
      next: result => { this.courses = result; this.requestDone(); },
      error: () => this.requestDone()
    })
  }

  getPaths(): void {
    this.filter.status = Status.Active;
    this.pathDiplomaService.getPaging(this.filter , 'isDiploma=false').subscribe({
      next: result => { this.paths = result; this.requestDone(); },
      error: () => this.requestDone()
    })
  }
  
  getDiplomas(): void {
    this.filter.status = Status.Active;
    this.pathDiplomaService.getPaging(this.filter, 'isDiploma=true').subscribe({
      next: result => { this.diplomas = result; this.requestDone(); },
      error: () => this.requestDone()
    })
  }

  resetSearch(): void {
    this.searchValue = "";
    this.showSearch = false;
    this.hideSearch.emit(this.showSearch);
    this.onSearch();
  }
}
